import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Copy,
  Check,
  KeyRound,
  Eye,
  EyeOff,
  Trash2,
  Loader2,
  ExternalLink,
  Pencil,
  BookOpen,
} from 'lucide-react';
import { appApi } from '../utils/api';
import { useAppRights } from '../hooks/useAppRights';
import type { AppInfo } from '../types/api';

const ApplicationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, rightsLoaded } = useAppRights();
  const [apps, setApps] = useState<AppInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [perPage, setPerPage] = useState(10);

  // 创建弹窗
  const [showCreate, setShowCreate] = useState(false);
  const [newName, setNewName] = useState('');
  const [newNotifyUrl, setNewNotifyUrl] = useState('');
  const [creating, setCreating] = useState(false);
  // 通知/错误
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // 复制反馈
  const [copied, setCopied] = useState<string>('');
  // appkey 显隐
  const [revealed, setRevealed] = useState<Record<number, boolean>>({});
  // notify_url 行内编辑
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [savingId, setSavingId] = useState<number | null>(null);

  const fetchApps = useCallback(async () => {
    setLoading(true);
    try {
      const response = await appApi.getList(page, perPage);
      if (response.code === 1 && response.data) {
        setApps(Array.isArray(response.data.data) ? response.data.data : []);
        setLastPage(response.data.last_page || 1);
        setTotal(response.data.total || 0);
        setPerPage(response.data.per_page || 10);
      } else {
        setApps([]);
      }
    } catch (err) {
      console.error('Failed to fetch apps:', err);
      setApps([]);
    }
    setLoading(false);
  }, [page, perPage]);

  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  const flashMessage = (msg: string) => {
    setMessage(msg);
    setTimeout(() => setMessage(''), 3000);
  };

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(''), 1500);
    } catch {
      flashMessage('Copy failed');
    }
  };

  const handleCreate = async () => {
    setError('');
    if (!newName.trim()) {
      setError('Application name is required');
      return;
    }
    if (newNotifyUrl.trim() && !/^https?:\/\//.test(newNotifyUrl.trim())) {
      setError('Notify URL must start with http:// or https://');
      return;
    }
    setCreating(true);
    try {
      const response = await appApi.create(newName.trim(), newNotifyUrl.trim());
      if (response.code === 1 && response.data) {
        setShowCreate(false);
        setNewName('');
        setNewNotifyUrl('');
        flashMessage('Application created');
        setPage(1);
        await fetchApps();
      } else {
        setError(response.msg || 'Failed to create application');
      }
    } catch {
      setError('Network error, please try again');
    }
    setCreating(false);
  };

  const handleUpdateNotifyUrl = async (app: AppInfo) => {
    setSavingId(app.id);
    setError('');
    try {
      const response = await appApi.updateNotifyUrl(app.id, editUrl.trim());
      if (response.code === 1) {
        setEditingId(null);
        flashMessage('Notify URL updated');
        await fetchApps();
      } else {
        setError(response.msg || 'Failed to update');
      }
    } catch {
      setError('Network error, please try again');
    }
    setSavingId(null);
  };

  const handleDelete = async (app: AppInfo) => {
    if (!window.confirm(`Delete application "${app.name}"?`)) return;
    setError('');
    try {
      const response = await appApi.delete(app.id);
      if (response.code === 1) {
        flashMessage('Application deleted');
        await fetchApps();
      } else {
        setError(response.msg || 'Failed to delete');
      }
    } catch {
      setError('Network error, please try again');
    }
  };

  const startEdit = (app: AppInfo) => {
    setEditUrl(app.notify_url || '');
    setEditingId(app.id);
  };

  const formatTime = (timestamp: number) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  const maskKey = (key: string) => key.slice(0, 8) + '••••••••••••' + key.slice(-8);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Applications</h1>
          <p className="text-zinc-400 mt-1">
            Create applications to integrate the payment gateway. Copy your AppID / AppKey and
            configure the payment notify URL.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/api-docs')}
            className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white rounded-lg transition-colors"
          >
            <BookOpen size={18} />
            API Docs
          </button>
          {canCreate && (
            <button
              onClick={() => {
                setError('');
                setShowCreate(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] hover:bg-[#7A73FF] text-white rounded-lg transition-colors"
            >
              <Plus size={18} />
              Create Application
            </button>
          )}
        </div>
      </div>

      {rightsLoaded && !canCreate && (
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-sm text-zinc-400 flex items-center gap-2">
          <KeyRound size={16} className="text-zinc-500" />
          Your account does not have permission to create applications. Contact the platform to get
          payment gateway access.
        </div>
      )}

      {message && (
        <div className="bg-emerald-400/10 border border-emerald-400/20 rounded-xl p-4 text-emerald-400">
          {message}
        </div>
      )}
      {error && (
        <div className="bg-red-400/10 border border-red-400/20 rounded-xl p-4 text-red-400">
          {error}
        </div>
      )}

      {/* Create Modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 border border-zinc-800 rounded-xl w-full max-w-md p-6">
            <h2 className="text-lg font-semibold text-white mb-4">Create Application</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-zinc-400 text-sm mb-2">Application Name</label>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="e.g. My Store"
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
                />
              </div>
              <div>
                <label className="block text-zinc-400 text-sm mb-2">
                  Payment Notify URL <span className="text-zinc-600">(https://...)</span>
                </label>
                <input
                  type="url"
                  value={newNotifyUrl}
                  onChange={(e) => setNewNotifyUrl(e.target.value)}
                  placeholder="https://merchant.com/pay/notify"
                  className="w-full px-4 py-2.5 bg-zinc-800 border border-zinc-700 rounded-lg text-white placeholder-zinc-500 focus:outline-none focus:border-zinc-600 transition-colors"
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  onClick={() => setShowCreate(false)}
                  disabled={creating}
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={creating}
                  className="flex items-center gap-2 px-4 py-2 bg-[#635BFF] hover:bg-[#7A73FF] disabled:opacity-50 text-white rounded-lg transition-colors"
                >
                  {creating ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Apps Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Name</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">AppID</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">AppKey</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Notify URL</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Status</th>
                <th className="text-left text-zinc-400 text-sm font-medium px-6 py-4">Created</th>
                <th className="text-right text-zinc-400 text-sm font-medium px-6 py-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    Loading...
                  </td>
                </tr>
              ) : apps.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center py-12 text-zinc-400">
                    {canCreate
                      ? 'No applications yet. Create one to get started.'
                      : 'No applications. Your account does not have access to the payment gateway yet.'}
                  </td>
                </tr>
              ) : (
                apps.map((app) => (
                  <tr
                    key={app.id}
                    className="border-b border-zinc-800 last:border-0 hover:bg-zinc-800/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-white text-sm font-medium">
                        <KeyRound size={16} className="text-zinc-500" />
                        {app.name}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-300 text-sm font-mono truncate max-w-[160px]" title={app.appid}>
                          {app.appid}
                        </span>
                        <button
                          onClick={() => handleCopy(app.appid, `appid-${app.id}`)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors"
                          title="Copy AppID"
                        >
                          {copied === `appid-${app.id}` ? (
                            <Check size={14} className="text-emerald-400" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <span className="text-zinc-300 text-sm font-mono">
                          {revealed[app.id] ? app.appkey : maskKey(app.appkey)}
                        </span>
                        <button
                          onClick={() => setRevealed((prev) => ({ ...prev, [app.id]: !prev[app.id] }))}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors"
                          title={revealed[app.id] ? 'Hide AppKey' : 'Show AppKey'}
                        >
                          {revealed[app.id] ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          onClick={() => handleCopy(app.appkey, `appkey-${app.id}`)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors"
                          title="Copy AppKey"
                        >
                          {copied === `appkey-${app.id}` ? (
                            <Check size={14} className="text-emerald-400" />
                          ) : (
                            <Copy size={14} />
                          )}
                        </button>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {editingId === app.id ? (
                        <div className="flex items-center gap-2">
                          <input
                            type="url"
                            value={editUrl}
                            onChange={(e) => setEditUrl(e.target.value)}
                            className="w-48 px-2 py-1 bg-zinc-800 border border-zinc-700 rounded-md text-white text-xs font-mono focus:outline-none focus:border-zinc-600"
                          />
                          <button
                            onClick={() => handleUpdateNotifyUrl(app)}
                            disabled={savingId === app.id}
                            className="px-2 py-1 bg-[#635BFF] hover:bg-[#7A73FF] disabled:opacity-50 text-white text-xs rounded-md"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingId(null)}
                            className="px-2 py-1 bg-zinc-800 hover:bg-zinc-700 text-white text-xs rounded-md"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400 text-xs font-mono truncate max-w-[160px]" title={app.notify_url}>
                            {app.notify_url || <span className="text-zinc-600">Not set</span>}
                          </span>
                          {app.notify_url && (
                            <a
                              href={app.notify_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 rounded-md text-zinc-500 hover:text-white transition-colors"
                            >
                              <ExternalLink size={14} />
                            </a>
                          )}
                          <button
                            onClick={() => startEdit(app)}
                            className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors"
                            title="Edit Notify URL"
                          >
                            <Pencil size={14} />
                          </button>
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {app.status === 1 ? (
                        <span className="px-2 py-1 rounded text-xs font-medium text-emerald-400 bg-emerald-400/10">
                          Enabled
                        </span>
                      ) : (
                        <span className="px-2 py-1 rounded text-xs font-medium text-zinc-400 bg-zinc-400/10">
                          Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-zinc-400 text-sm">{formatTime(app.createtime)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => navigate(`/admin/api-docs?app_id=${app.id}`)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-white hover:bg-zinc-700 transition-colors"
                          title="API Documentation"
                        >
                          <BookOpen size={16} />
                        </button>
                        <button
                          onClick={() => handleDelete(app)}
                          className="p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-zinc-700 transition-colors"
                          title="Delete Application"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <div className="text-zinc-400 text-sm">
          {total > 0 ? `Total ${total} applications` : 'No applications'}
        </div>
        {lastPage > 1 && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1 || loading}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              Prev
            </button>
            <span className="text-zinc-400 text-sm px-2">
              Page {page} / {lastPage}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
              disabled={page >= lastPage || loading}
              className="px-3 py-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm transition-colors"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApplicationsPage;
